<template>
  <div>
    <transition name="slide-in-left">
      <div 
        v-show="$store.state.isBottomBarVisible"
        class="right-sidebar"
        :class="{ 'dir-rtl' : language.rtl }"
        :style="`
          background: ${styles['right-sidebar']['background-colour']};
        `"
        @click="clearTimeout"
      >
        <transition name="fade">
          <div
            v-if="!activeContent.isFilterHidden && activeContent.filterType"
            class="slide-out"
            :class="{ 'slide-out--visible': isSlideOutVisible }"
          >
            <v-filter :type="activeContent.filterType"/>
            <div class="slide-out__toggle-button" @click="toggleSlideOut"><fa :icon="['far', 'chevron-left']" /></div>
          </div>
        </transition>

        <div class="right-sidebar__brand-logo">
          <img :src="`${$store.state.assetsPath}${config.generalSidebarBrandLogo}`">
        </div>

        <div class="right-sidebar__layout">
          <div class="right-sidebar__spacer"></div>

          <div v-if="!activeContent.sidebarMapImage">
            <transition-group
              name="sidebar-fade"
              tag="div"
              class="right-sidebar__content"
            >
              <div v-if="activeContent.minimap" :key="activeContent.minimap" class="right-sidebar__minimap-wrapper">
                <img :src="`${$store.state.assetsPath}${activeContent.minimap}`" :key="activeContent.minimap" class="right-sidebar__minimap">
              </div>
              <div
                v-if="activeContent.heading"
                :key="activeContent.heading"
                class="right-sidebar__heading"
                v-html="activeContent.heading"
                :style="`
                  color: ${styles['right-sidebar']['heading-colour']};
                  font-size: ${styles['right-sidebar']['heading-font-size']};
                  font-weight: ${styles['right-sidebar']['heading-font-weight']};
                `"
              />
              <div
                v-if="activeContent.description"
                :key="activeContent.description"
                class="right-sidebar__description"
                v-html="activeContent.description"
                :style="`
                  color: ${styles['right-sidebar']['description-colour']};
                  font-size: ${styles['right-sidebar']['description-font-size']};
                  font-weight: ${styles['right-sidebar']['description-font-weight']};
                  line-height: ${styles['right-sidebar']['description-line-height']};
                `"
              />
              <div
                v-if="activeContent.markerGuide"
                :key="activeContent.markerGuide"
                class="right-sidebar__marker-guide"
                :style="`
                  color: ${styles['right-sidebar']['description-colour']};
                `"
              >
                <div class="guide">
                  <div class="guide__icon guide__icon--available">
                    <fa class="check" :icon="['far', 'check']" />
                  </div>
                  <div class="guide__label">Available</div>
                </div>
                <div v-if="env.VUE_APP_STYLE_TEMPLATE !== 'brentwoodforest' && env.VUE_APP_STYLE_TEMPLATE !== 'amara'" class="guide">
                  <div class="guide__icon guide__icon--builder">
                    <fa class="check" :icon="['far', 'check']" />
                  </div>
                  <div class="guide__label">Builder Lots</div>
                </div>
                <div class="guide">
                  <div class="guide__icon guide__icon--on-hold">
                    <fa class="line" :icon="['fas', 'horizontal-rule']" />
                  </div>
                  <div class="guide__label">On Hold</div>
                </div>
                <div class="guide">
                  <div class="guide__icon guide__icon--sold">
                    <fa class="line" :icon="['far', 'times']" />
                  </div>
                  <div class="guide__label">Sold</div>
                </div>
              </div>
              <div
                v-if="activeContent.qrUrl"
                :key="activeContent.qrUrl"
                class="right-sidebar__qr-wrapper"
              >
                <div class="right-sidebar__qr">
                  <div class="right-sidebar__qr-heading" v-html="activeContent.qrHeading" />
                  <div class="right-sidebar__qr-row">
                    <qrcode-vue
                      class="qrcode"
                      :value="activeContent.qrUrl"
                      size="1000"
                      renderAs="canvas" 
                      :key="activeContent.qrUrl"
                    />
                    <div class="right-sidebar__qr-message">{{ activeContent.qrMessage }}</div>
                  </div>
                </div>
              </div>
              <div
                v-if="activeContent.virtualTourUrl"
                :key="activeContent.virtualTourUrl"
                class="right-sidebar__qr-wrapper"
              >
                <div class="right-sidebar__qr">
                  <div class="right-sidebar__qr-row">
                    <button class="btn touch" @click="$store.commit('property/SET_ACTIVE_VIRTUAL_TOUR_URL', activeContent.virtualTourUrl)">{{ activeContent.virtualTourButtonLabel }}</button>
                  </div>
                </div>
              </div>

              <div v-if="activeContent.activeBackLink" :key="activeContent.activeBackLink" class="right-sidebar__back-wrapper">
                <div class="right-sidebar__back">
                  <router-link
                    tag="button"
                    class="btn btn--small touch"
                    :to="activeContent.activeBackLink"
                    :exact="true"
                  >
                    <span><fa :icon="['fas', 'chevron-left']" /> Back</span> 
                  </router-link>
                </div>
              </div>
            </transition-group>

            <div v-if="activeSubsection && activeSubsection.issuuEmbedUrl" class="right-sidebar__back-wrapper">
              <button
                v-if="!$store.state.actions.showIssuuUrl"
                class="btn touch"
                @click="$store.commit('actions/SET_ISSUU_URL', activeSubsection.issuuEmbedUrl); $store.commit('sidebar/CLOSE_SLIDEOUT')"
              >
                <span>View Brochure</span>
              </button>
              <button
                v-else
                class="btn touch"
                @click="$store.commit('actions/SET_ISSUU_URL', '')"
              >
                <span>Close Brochure</span>
              </button>
            </div>

            <div v-if="activeContent && activeContent.issuuEmbedUrl" class="right-sidebar__back-wrapper">
              <button
                v-if="!$store.state.actions.showIssuuUrl"
                class="btn touch"
                @click="$store.commit('actions/SET_ISSUU_URL', activeContent.issuuEmbedUrl); $store.commit('sidebar/CLOSE_SLIDEOUT')"
              >
                <span>View Brochure</span>
              </button>
              <button
                v-else
                class="btn touch"
                @click="$store.commit('actions/SET_ISSUU_URL', '')"
              >
                <span>Close Brochure</span>
              </button>
            </div>

            <div v-if="styles['general']['disclaimer']" class="right-sidebar__disclaimer" v-html="styles['general']['disclaimer']"></div>
          </div>      

          <transition-group
            name="sidebar-fade"
            tag="div"
            class="right-sidebar__content"
          >
            <div v-if="activeContent.sidebarMapImage" :key="activeContent.sidebarMapImage" class="right-sidebar__sidebar-map-image-wrapper">
              <img :src="`${$store.state.assetsPath}${activeContent.sidebarMapImage}`" class="right-sidebar__sidebar-map-image">
            </div>
          </transition-group>

          <div v-for="iframe in activeContent.iframes" :key="iframe.url" class="right-sidebar__back-wrapper">
            <div class="right-sidebar__back" v-if="!videoWallIframePlaying">
              <button
                class="btn touch"
                @click="$store.dispatch('triggerIframe', iframe.url)"
              >
                <span>{{ iframe.label }}</span> 
              </button>
            </div>
          </div>
          <div v-if="videoWallIframePlaying" class="right-sidebar__back-wrapper">
            <div class="right-sidebar__back">
              <button
                class="btn touch"
                @click="$store.dispatch('triggerIframe', true)"
              >
                <span>{{ 'Close View' }}</span> 
              </button>
            </div>
          </div>         
          <div v-if="showSimpleBacklink" class="right-sidebar__back-wrapper">
            <div class="right-sidebar__back">
              <button
                class="btn btn--small touch"
                @click="getBacklink"
              >
                <span><fa :icon="['fas', 'chevron-left']" /> Back</span> 
            </button>
            </div>
          </div>           
        </div>           
        <div v-if="config.generalSidebarLogo" class="right-sidebar__logo">
          <img :src="`${$store.state.assetsPath}${config.generalSidebarLogo}`">
        </div>
      </div>
    </transition>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import QrcodeVue from 'qrcode.vue'
import VFilter from '@/components/VFilter'
import _ from 'lodash'
import { bus } from '../main'

export default {
  props: ['content'],

  components: { QrcodeVue, VFilter },

  data () {
    return {
      activeContent: {}
    }
  },

  computed: {
    ...mapGetters({
      activeSubsection: 'property/activeSubsection',
      activeSubsectionGroup: 'property/activeSubsectionGroup',
      activeLivingOption: 'property/activeLivingOption',
      config: 'config',
      styles: 'styleTemplate',
      showSimpleBacklink: 'showSimpleBacklink',
      shouldSlideOutAutoOpen: 'sidebar/shouldSlideOutAutoOpen'
    }),

    videoWallIframePlaying () {
      return this.$store.state.videoWallIframePlaying
    },

    activeSubsectionId () {
      return this.$store.state.route.query.activeSubsectionId
    },

    isSlideOutVisible () {
      return this.$store.getters['sidebar/isSlideOutVisible']
    },

    language () {
      return this.$store.getters.language
    },

    env () {
      return process.env
    }
  },

  watch: {
    content: {
      handler (newContent, oldContent) {
        if (!_.isEqual(oldContent, newContent)) {
          if (!_.isEmpty(oldContent)) {
            this.activeContent = {}
          }

          if (!_.isEmpty(newContent)) {
            this.$store.commit('addAnimationToQueue', 'SideBar')
          }
        }
      },
      immediate: true
    }
  },

  mounted () {
    bus.$on('animateSideBar', () => {
      if (!_.isEqual(this.activeContent, this.content)) {
        setTimeout(() => {
          this.activeContent = this.content
          bus.$emit('finishedAnimation')
        }, 500)
      } else {
        bus.$emit('finishedAnimation')
      }

      if (this.shouldSlideOutAutoOpen) {
        setTimeout(() => {
          this.$store.commit('sidebar/OPEN_SLIDEOUT')
        }, 2000)
      }
    })
  },

  methods: {
    clearTimeout () {
      this.$store.commit('sidebar/CLEAR_SIDEBAR_CLOSE_TIMEOUT')
    },

    isNavItemActive (item) {
      if (this.$route.fullPath === item.to) {
        return true
      }
    },

    toggleSlideOut () {
      if (!this.$store.state.property.activeVirtualTourUrl) {
        if (this.isSlideOutVisible) {
          this.$store.commit('sidebar/CLOSE_SLIDEOUT')
        } else {
          this.$store.commit('sidebar/OPEN_SLIDEOUT')
        }
      }
    },

    getBacklink () {
      if (this.activeSubsectionGroup) {
        this.$router.push(`/category/6302c384c92da0602163fe71/masterplan/lo/` + this.activeSubsectionGroup.parent.id)
      }
      if (this.activeSubsection) {
        this.$router.push(`/category/6302c384c92da0602163fe71/masterplan/sg/` + this.activeSubsection.parent.id)
      }
    }
  }
}
</script>
<style lang="scss" scoped>
@import "@/styles/_mixins.scss";
@import "@/styles/_variables.scss";
@import "@/styles/_buttons.scss";

.right-sidebar {
  height: 98rem;
  position: absolute;
  right: 0;
  top: 0;
  width: 32.5rem;
  z-index: 100;

  &__layout {
    background: $sidebar-bg-color;
    display: grid;
    padding: 0 0 5rem;
    position: relative;
    grid-gap: 0;
    grid-template-columns: 1fr;
    grid-template-rows: 37rem 1fr;
    height: 100%;
    overflow: hidden;
  }

  &__content {
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
    z-index: 10;
  }

  &__brand-logo {
    position: absolute;
    right: 0;
    top: 0;
    width: 100%;
    z-index: 10;

    img {
      height: 100%;
      object-fit: contain;
      width: 100%;
    }
  }

  &__logo {
    bottom: 0;
    height: 5rem;
    position: absolute;
    right: 0;
    width: calc(100% - 5rem);
    z-index: 20;

    img {
      height: 100%;
      object-fit: contain;
      object-position: bottom right;
      width: 100%;
    }
  }  

  &__minimap-wrapper {
    position: relative;
    margin: 0 5rem 2rem 0;
    height: 16rem;
  }

  &__minimap {
    position: absolute;
    max-width: 100%;
    max-height: 100%;
    top: 0;
    left: 0;
    border-top-right-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
    z-index: 500;
  }

  &__sidebar-map-image-wrapper {
    position: relative;
    margin: 0 2.5rem;
    height: 100%;
    width: 100%;
  }

  &__sidebar-map-image {
    position: absolute;
    max-width: 100%;
    max-height: 100%;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    z-index: 500;
  }

  &__heading {
    align-items: flex-end;
    color: $lime;
    display: flex;
    font-size: 1.8rem;
    font-weight: 400;
    letter-spacing: 0.05em;
    line-height: 1.2;
    margin: 0 7rem 1rem 5rem;
    top: 0;
    left: 0;
    text-transform: uppercase;
  }

  &__description-wrapper {
    position: relative;
  }

  &__description {
    font-size: 1.1rem;
    font-weight: 400;
    letter-spacing: 0.05em;
    line-height: 1.5rem;
    margin: 0 0 1rem;
    padding-left: 5rem;
    padding-right: 7rem;
    width: 100%;

    /deep/ ul {
      padding-inline-start: 0;
    }
  }

  &__disclaimer {
    bottom: 0;
    font-size: .5rem;
    font-weight: 400;
    letter-spacing: 0.05em;
    line-height: 1.1em;
    margin: 0 0 1rem;
    opacity: .15;
    padding-left: 2rem;
    padding-right: 2rem;
    position: absolute;
    font-style: italic;
    width: 100%;
    z-index: 10;
  }

  &__marker-guide {
    font-size: 1.1rem;
    font-weight: 400;
    letter-spacing: 0.05em;
    line-height: 1.5rem;
    margin: 2rem 7rem 1rem 5rem;
    width: 100%;

    .guide {
      align-items: center;
      display: flex;
      margin: 0 0 1rem;

      &__icon {
        border-radius: 50%;
        box-shadow: -0.2rem 0.3rem 0.3rem rgba(0, 0, 0, .35);
        height: 2rem;
        position: relative;
        width: 2rem;

        &--available {
          background: $marker-available;

          svg {
            color: $black;
          }
        }

        &--builder {
          background: $marker-builder;

          svg {
            color: $black;
          }
        }

        &--on-hold {
          background: $marker-on-hold;

          svg {
            width: 60%;
          }
        }

        &--sold {
          background: $marker-sold;
        }

        svg {
          font-size: 1.2em;
          left: 50%;
          position: absolute;
          top: 50%;
          transform: translate(-50%, -50%);
        }
      }

      &__label {
        font-size: .8rem;
        margin: 0 0 0 .75rem;
        text-transform: uppercase;
      }
    }
  }

  &__back-wrapper {
    margin: auto 0 0;
    padding-left: 5rem;
    padding-right: 7rem;
    width: 100%;
    z-index: 10;
  }

  &__back {
    font-size: 1.1rem;
    font-weight: 400;
    height: 100%;
    letter-spacing: 0.05em;
    line-height: 1.5rem;
    overflow: hidden;
    padding: 1rem 0;
    width: 100%;

    svg {
      margin-right: .25rem;
    }
  }

  &__qr-wrapper {
    position: relative;
  }

  &__qr {
    margin: 2rem 0 1rem;
    padding-left: 5rem;
    padding-right: 7rem;
    width: 100%;

    &-heading {
      font-size: 1.2rem;
      font-weight: 200;
      margin-bottom: 1.2rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    &-row {
      display: flex;
    }

    &-message {
      font-size: 1.1rem;
      font-weight: 400;
      letter-spacing: 0.05em;
      line-height: 1.5rem;
      padding-left: 1.75rem;
      padding-right: 4rem;
    }
  }
}

.slide-out {
  background: rgba($black, 0.8);
  border-radius: 0.5rem 0 0 0.5rem;
  bottom: 10rem;
  padding: 3rem;
  pointer-events: all;
  position: absolute;
  right: 100%;
  transform: translateX(calc(100% - 2rem));
  transition: all 0.6s ease-in-out;
  width: 25rem;

  &--visible {
    transform: none;

    svg {
      transform: scaleX(-1);
    }
  }

  svg {
    transition: transform 0.6s ease-in-out;
  }
  
  &__toggle-button {
    align-items: center;
    background: rgba($black, 0.8);
    border-radius: 0.5rem 0 0 0.5rem;
    bottom: 4rem;
    display: flex;
    font-size: 2rem;
    height: 4rem;
    padding-left: 1.3rem;
    pointer-events: auto;
    position: absolute;
    right: 100%;
    width: 4rem;
  }
}

.qrcode {
  background: $white;
  display: inline-block;
  padding: 0.4rem;
  

  /deep/ canvas {
    height: 10rem !important;
    width: 10rem !important;
  }
}

.bottom-bar__nav-item-wrapper a {
  pointer-events: auto;
}
</style>